// Firebase (app, App Check, Firestore) pesa piu' di 200 KB e serve solo quando
// il widget viene davvero usato: si carica al primo utilizzo, non con la pagina.
let firestorePromise = null;
function caricaFirestore() {
  if (!firestorePromise) {
    firestorePromise = Promise.all([import('firebase/firestore'), import('/js/firebaseInit.js')])
      .then(([firestore, { firebaseInit }]) => ({ firestore, firebaseInit }));
  }
  return firestorePromise;
}
import { t, PAGE_LANG, registerTranslations } from '/js/i18n.js';

// Dizionario locale del widget recensioni (chiavi = stringhe italiane).
registerTranslations({
  'Nascondi recensione per 24 ore': 'Hide the review prompt for 24 hours',
  'questo tool': 'this tool',
  'Raccontaci la tua esperienza': 'Tell us about your experience',
  "Com'è andata con {tool}? La tua opinione ci aiuta a migliorare l'esperienza.":
    'How did it go with {tool}? Your feedback helps us improve the experience.',
  'Valuta {tool} da 1 a 5 stelle': 'Rate {tool} from 1 to 5 stars',
  '{n} stelle': '{n} stars',
  'Chiudi recensione': 'Close review',
  'Grazie per il voto': 'Thanks for your rating',
  'Vuoi aggiungere una recensione?': 'Would you like to add a review?',
  'Il tuo nome': 'Your name',
  'Es. Marco': 'E.g. Sarah',
  'Opzionale: se lasci vuoto, useremo “Anonimo”.': 'Optional: if you leave it blank, we will use “Anonymous”.',
  'Recensione breve': 'Short review',
  '(opzionale)': '(optional)',
  'Cosa ti è piaciuto? Cosa possiamo migliorare?': 'What did you like? What could we improve?',
  'Massimo {n} caratteri.': 'Maximum {n} characters.',
  'Più tardi': 'Maybe later',
  'Conferma recensione': 'Submit review',
  'Ci dispiace': 'Sorry to hear that',
  'Cosa non ha funzionato?': 'What went wrong?',
  'Raccontaci il problema': 'Tell us what happened',
  '(obbligatorio)': '(required)',
  'Es. il calcolo non torna, la pagina non si carica, manca una funzione...':
    'E.g. the numbers look wrong, the page does not load, a feature is missing...',
  'Annulla': 'Cancel',
  'Invia voto e feedback': 'Send rating and feedback',
  'Hai selezionato 1 stella.': 'You selected 1 star.',
  'Hai selezionato {n} stelle.': 'You selected {n} stars.',
  'Abbiamo mantenuto il tuo voto precedente.': 'We kept your previous rating.',
  'Voto non registrato. Se cambi idea, raccontaci cosa è andato storto.':
    'Rating not saved. If you change your mind, tell us what went wrong.',
  'Anonimo': 'Anonymous',
  'Raccontaci cosa non ha funzionato per registrare il voto.':
    'Tell us what went wrong to register your rating.',
  'Salvataggio del voto in corso...': 'Saving your rating...',
  'Voto salvato. Puoi aggiungere una recensione se vuoi.': 'Rating saved. You can add a review if you like.',
  'Non siamo riusciti a salvare il voto. Riprova tra poco.':
    'We could not save your rating. Please try again shortly.',
  'Scrivi almeno qualche parola sul problema (minimo {n} caratteri).':
    'Please write at least a few words about the problem (minimum {n} characters).',
  'Salvataggio in corso...': 'Saving...',
  'Grazie, recensione salvata.': 'Thanks, your review has been saved.',
  'Recensione salvata. Grazie!': 'Review saved. Thank you!',
  'Non siamo riusciti a salvare la recensione. Riprova tra poco.':
    'We could not save your review. Please try again shortly.',
  'Hai già lasciato un voto. Puoi aggiornarlo quando vuoi.':
    'You have already left a rating. You can update it anytime.',
  'Recensioni temporaneamente non disponibili.': 'Reviews are temporarily unavailable.'
});

const COLLECTION_NAME = 'toolReviews';
const GA_MEASUREMENT_ID = 'G-HHXEQQP7MF';
const LOCAL_CLIENT_KEY = 'stipendee_tool_review_client_id';
const DISMISSED_KEY_PREFIX = 'stipendee_tool_review_dismissed_until';
const MAX_COMMENT_LENGTH = 1000;
const MAX_DISPLAY_NAME_LENGTH = 60;
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;
const LOW_RATING_THRESHOLD = 2;
const LOW_RATING_MIN_COMMENT_LENGTH = 10;

let modalElements = null;
let activeWidget = null;

const clampRating = (value) => {
  const rating = Number.parseInt(value, 10);
  if (!Number.isInteger(rating)) return null;
  return Math.max(1, Math.min(5, rating));
};

const escapeHtml = (value) => String(value)
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

async function sha256(value) {
  if (!crypto?.subtle) {
    let hash = 0;
    const input = String(value);
    for (let index = 0; index < input.length; index += 1) {
      hash = ((hash << 5) - hash) + input.charCodeAt(index);
      hash |= 0;
    }
    return `fallback-${Math.abs(hash).toString(16)}`;
  }

  const encoder = new TextEncoder();
  const data = encoder.encode(value);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function getLocalClientId() {
  try {
    let clientId = localStorage.getItem(LOCAL_CLIENT_KEY);
    if (!clientId) {
      clientId = crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
      localStorage.setItem(LOCAL_CLIENT_KEY, clientId);
    }
    return clientId;
  } catch (error) {
    return `session-${Date.now()}-${Math.random()}`;
  }
}

function getGaClientId() {
  return new Promise((resolve) => {
    if (typeof window.gtag !== 'function') {
      resolve(null);
      return;
    }

    const timeout = window.setTimeout(() => resolve(null), 1200);
    window.gtag('get', GA_MEASUREMENT_ID, 'client_id', (clientId) => {
      window.clearTimeout(timeout);
      resolve(clientId || null);
    });
  });
}

async function getClientIdentity() {
  const gaClientId = await getGaClientId();
  const source = gaClientId ? 'ga' : 'local';
  const rawClientId = gaClientId || getLocalClientId();
  const clientIdHash = await sha256(rawClientId);
  return { clientIdHash, source };
}

async function getDb() {
  const { firestore, firebaseInit } = await caricaFirestore();
  const { app } = firebaseInit();
  return { db: firestore.getFirestore(app), firestore };
}

function getDismissedKey(toolSlug) {
  return `${DISMISSED_KEY_PREFIX}:${toolSlug}`;
}

function isDismissed(toolSlug) {
  try {
    const dismissedUntil = Number.parseInt(localStorage.getItem(getDismissedKey(toolSlug)), 10);
    return Number.isFinite(dismissedUntil) && dismissedUntil > Date.now();
  } catch (error) {
    return false;
  }
}

function dismissWidget(widget) {
  try {
    localStorage.setItem(getDismissedKey(widget.toolSlug), String(Date.now() + DISMISS_DURATION_MS));
  } catch (error) {
    console.warn('Impossibile salvare la chiusura recensioni', error);
  }
  widget.container.hidden = true;
  widget.container.innerHTML = '';
}

function buildWidget(container) {
  const toolName = container.dataset.toolName || t('questo tool');
  const hideDismiss = container.dataset.toolReviewHideDismiss === 'true';
  const dismissButton = hideDismiss ? '' : `
    <button type="button" class="tool-review__dismiss" aria-label="${t('Nascondi recensione per 24 ore')}">
      <i class="ph ph-x" aria-hidden="true"></i>
    </button>
  `;

  container.classList.add('tool-review');
  container.innerHTML = `
    ${dismissButton}
    <div class="tool-review__copy">
      <h2>${t('Raccontaci la tua esperienza')}</h2>
      <p>${t("Com'è andata con {tool}? La tua opinione ci aiuta a migliorare l'esperienza.", { tool: escapeHtml(toolName) })}</p>
    </div>
    <div class="tool-review__stars" role="radiogroup" aria-label="${t('Valuta {tool} da 1 a 5 stelle', { tool: escapeHtml(toolName) })}"></div>
    <p class="tool-review__status" aria-live="polite"></p>
  `;

  const stars = container.querySelector('.tool-review__stars');
  for (let rating = 1; rating <= 5; rating += 1) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'tool-review__star';
    button.dataset.rating = String(rating);
    button.setAttribute('role', 'radio');
    button.setAttribute('aria-checked', 'false');
    button.setAttribute('aria-label', t('{n} stelle', { n: rating }));
    button.textContent = '★';
    stars.appendChild(button);
  }
}

function setStatus(widget, message, type = '') {
  if (!widget.statusEl) return;
  widget.statusEl.textContent = message;
  widget.statusEl.dataset.status = type;
}

function updateStars(widget, rating) {
  widget.rating = rating;
  widget.starButtons.forEach((button) => {
    const starRating = Number.parseInt(button.dataset.rating, 10);
    const isSelected = starRating <= rating;
    button.classList.toggle('is-selected', isSelected);
    button.setAttribute('aria-checked', String(starRating === rating));
  });
}

function getCommentValue() {
  const value = modalElements?.comment?.value || '';
  return value.slice(0, MAX_COMMENT_LENGTH).trim();
}

function getDisplayNameValue() {
  const value = modalElements?.displayName?.value || '';
  return value.slice(0, MAX_DISPLAY_NAME_LENGTH).trim();
}

function updateCommentCounter() {
  if (!modalElements?.counter || !modalElements?.comment) return;
  const length = modalElements.comment.value.length;
  modalElements.counter.textContent = `${length}/${MAX_COMMENT_LENGTH}`;
}

function ensureModal() {
  if (modalElements) return modalElements;

  const modal = document.createElement('div');
  modal.className = 'tool-review-modal';
  modal.id = 'toolReviewModal';
  modal.setAttribute('aria-hidden', 'true');
  modal.innerHTML = `
    <div class="tool-review-modal__backdrop" data-tool-review-close></div>
    <div class="tool-review-modal__panel" role="dialog" aria-modal="true" aria-labelledby="toolReviewModalTitle">
      <button type="button" class="tool-review-modal__close" data-tool-review-close aria-label="${t('Chiudi recensione')}">
        <i class="ph ph-x" aria-hidden="true"></i>
      </button>
      <p class="tool-review__eyebrow">${t('Grazie per il voto')}</p>
      <h2 id="toolReviewModalTitle">${t('Vuoi aggiungere una recensione?')}</h2>
      <p class="tool-review-modal__rating"></p>
      <label class="tool-review-modal__field" for="toolReviewDisplayName">
        <span>${t('Il tuo nome')}</span>
        <input id="toolReviewDisplayName" type="text" maxlength="${MAX_DISPLAY_NAME_LENGTH}" placeholder="${t('Es. Marco')}">
        <small>${t('Opzionale: se lasci vuoto, useremo “Anonimo”.')}</small>
      </label>
      <label class="tool-review-modal__field" for="toolReviewComment">
        <span class="tool-review-modal__comment-label">${t('Recensione breve')} <small>${t('(opzionale)')}</small></span>
        <textarea id="toolReviewComment" rows="5" maxlength="${MAX_COMMENT_LENGTH}" placeholder="${t('Cosa ti è piaciuto? Cosa possiamo migliorare?')}"></textarea>
      </label>
      <div class="tool-review-modal__meta">
        <span>${t('Massimo {n} caratteri.', { n: MAX_COMMENT_LENGTH })}</span>
        <span class="tool-review-modal__counter">0/${MAX_COMMENT_LENGTH}</span>
      </div>
      <p class="tool-review-modal__status" aria-live="polite"></p>
      <div class="tool-review-modal__actions">
        <button type="button" class="myButton white-button" data-tool-review-close>
          <span class="tool-review-modal__cancel-label">${t('Più tardi')}</span>
        </button>
        <button type="button" class="myButton" data-tool-review-submit>
          <span class="button-text">${t('Conferma recensione')}</span>
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  modalElements = {
    modal,
    eyebrow: modal.querySelector('.tool-review__eyebrow'),
    title: modal.querySelector('#toolReviewModalTitle'),
    ratingText: modal.querySelector('.tool-review-modal__rating'),
    commentLabel: modal.querySelector('.tool-review-modal__comment-label'),
    displayName: modal.querySelector('#toolReviewDisplayName'),
    comment: modal.querySelector('#toolReviewComment'),
    counter: modal.querySelector('.tool-review-modal__counter'),
    status: modal.querySelector('.tool-review-modal__status'),
    cancelLabel: modal.querySelector('.tool-review-modal__cancel-label'),
    submit: modal.querySelector('[data-tool-review-submit]'),
    submitLabel: modal.querySelector('[data-tool-review-submit] .button-text')
  };

  modal.querySelectorAll('[data-tool-review-close]').forEach((button) => {
    button.addEventListener('click', closeModal);
  });
  modalElements.comment.addEventListener('input', () => {
    updateCommentCounter();
    updateSubmitState();
  });
  modalElements.submit.addEventListener('click', handleModalSubmit);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modal.classList.contains('is-open')) {
      closeModal();
    }
  });

  return modalElements;
}

function updateSubmitState() {
  if (!modalElements) return;
  const needsComment = Boolean(activeWidget?.pendingLowRating);
  modalElements.submit.disabled = needsComment && getCommentValue().length < LOW_RATING_MIN_COMMENT_LENGTH;
}

function configureModalMode(elements, isLowRating) {
  elements.eyebrow.textContent = isLowRating ? t('Ci dispiace') : t('Grazie per il voto');
  elements.title.textContent = isLowRating ? t('Cosa non ha funzionato?') : t('Vuoi aggiungere una recensione?');
  elements.commentLabel.innerHTML = isLowRating
    ? `${t('Raccontaci il problema')} <small>${t('(obbligatorio)')}</small>`
    : `${t('Recensione breve')} <small>${t('(opzionale)')}</small>`;
  elements.comment.placeholder = isLowRating
    ? t('Es. il calcolo non torna, la pagina non si carica, manca una funzione...')
    : t('Cosa ti è piaciuto? Cosa possiamo migliorare?');
  elements.cancelLabel.textContent = isLowRating ? t('Annulla') : t('Più tardi');
  elements.submitLabel.textContent = isLowRating ? t('Invia voto e feedback') : t('Conferma recensione');
}

function openModal(widget) {
  const elements = ensureModal();
  activeWidget = widget;
  configureModalMode(elements, Boolean(widget.pendingLowRating));
  elements.ratingText.textContent = widget.rating === 1
    ? t('Hai selezionato 1 stella.')
    : t('Hai selezionato {n} stelle.', { n: widget.rating });
  const isAnonymousName = widget.displayName === 'Anonimo' || widget.displayName === 'Anonymous';
  elements.displayName.value = isAnonymousName ? '' : widget.displayName || '';
  elements.comment.value = widget.comment || '';
  elements.status.textContent = '';
  updateCommentCounter();
  updateSubmitState();
  elements.modal.classList.add('is-open');
  elements.modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('tool-review-modal-open');
  window.setTimeout(() => elements.comment.focus(), 0);
}

function closeModal() {
  if (!modalElements) return;
  const widget = activeWidget;
  modalElements.modal.classList.remove('is-open');
  modalElements.modal.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('tool-review-modal-open');
  activeWidget = null;

  if (widget?.pendingLowRating) {
    widget.pendingLowRating = false;
    updateStars(widget, widget.savedRating || 0);
    setStatus(
      widget,
      widget.savedRating
        ? t('Abbiamo mantenuto il tuo voto precedente.')
        : t('Voto non registrato. Se cambi idea, raccontaci cosa è andato storto.'),
      ''
    );
  }
}

async function saveReview(widget, { status }) {
  const rating = clampRating(widget.rating);
  if (!rating || !widget.docRef) return;

  const comment = status === 'completed' ? getCommentValue() : widget.comment || '';
  const displayNameInput = status === 'completed' ? getDisplayNameValue() : widget.displayName || '';
  const displayName = displayNameInput || t('Anonimo');
  const timestamp = new Date().toISOString();
  const payload = {
    toolSlug: widget.toolSlug,
    toolName: widget.toolName,
    rating,
    comment,
    displayName,
    isAnonymous: !displayNameInput,
    lang: PAGE_LANG,
    clientIdHash: widget.clientIdHash,
    clientIdSource: widget.clientIdSource,
    status,
    pageUrl: window.location.href,
    baseUrl: window.location.origin,
    pageTitle: document.title,
    userAgent: navigator.userAgent,
    timestamp,
    updatedAt: timestamp
  };

  if (!widget.hasExistingReview) {
    payload.createdAt = timestamp;
  }

  await widget.firestore.setDoc(widget.docRef, payload, { merge: true });
  widget.hasExistingReview = true;
  widget.comment = comment;
  widget.displayName = displayName;
  widget.savedRating = rating;
  widget.pendingLowRating = false;
}

async function handleStarClick(widget, rating) {
  if (!(await widget.connect())) return;
  const parsedRating = clampRating(rating);
  if (!parsedRating) return;

  updateStars(widget, parsedRating);

  if (parsedRating <= LOW_RATING_THRESHOLD) {
    widget.pendingLowRating = true;
    setStatus(widget, t('Raccontaci cosa non ha funzionato per registrare il voto.'), '');
    openModal(widget);
    return;
  }

  widget.pendingLowRating = false;
  setStatus(widget, t('Salvataggio del voto in corso...'), '');
  openModal(widget);

  try {
    await saveReview(widget, { status: 'rating_only' });
    setStatus(widget, t('Voto salvato. Puoi aggiungere una recensione se vuoi.'), 'success');
  } catch (error) {
    console.warn('Salvataggio recensione fallito', error);
    setStatus(widget, t('Non siamo riusciti a salvare il voto. Riprova tra poco.'), 'error');
  }
}

async function handleModalSubmit() {
  if (!activeWidget) return;

  if (activeWidget.pendingLowRating && getCommentValue().length < LOW_RATING_MIN_COMMENT_LENGTH) {
    modalElements.status.textContent = t('Scrivi almeno qualche parola sul problema (minimo {n} caratteri).', { n: LOW_RATING_MIN_COMMENT_LENGTH });
    return;
  }

  modalElements.submit.disabled = true;
  modalElements.status.textContent = t('Salvataggio in corso...');
  try {
    await saveReview(activeWidget, { status: 'completed' });
    setStatus(activeWidget, t('Grazie, recensione salvata.'), 'success');
    modalElements.status.textContent = t('Recensione salvata. Grazie!');
    window.setTimeout(closeModal, 650);
  } catch (error) {
    console.warn('Salvataggio recensione fallito', error);
    modalElements.status.textContent = t('Non siamo riusciti a salvare la recensione. Riprova tra poco.');
  } finally {
    updateSubmitState();
  }
}

async function hydrateExistingReview(widget) {
  const snapshot = await widget.firestore.getDoc(widget.docRef);
  if (!snapshot.exists()) return;

  const data = snapshot.data();
  const rating = clampRating(data.rating);
  if (rating) {
    updateStars(widget, rating);
    widget.savedRating = rating;
  }
  widget.comment = typeof data.comment === 'string' ? data.comment : '';
  widget.displayName = typeof data.displayName === 'string' ? data.displayName : '';
  widget.hasExistingReview = true;
  setStatus(widget, t('Hai già lasciato un voto. Puoi aggiornarlo quando vuoi.'), 'success');
}

// Aggancia la logica recensioni a un container che ha gia' le stelline nel markup.
// Vale sia per il widget generato da buildWidget() sia per markup statico come la
// slide della promo card: gating, modale e salvataggio restano gli stessi.
async function setupWidget(container) {
  const toolSlug = container.dataset.toolSlug?.trim();
  const toolName = container.dataset.toolName || toolSlug;
  if (!toolSlug) return null;

  const widget = {
    container,
    toolSlug,
    toolName,
    rating: 0,
    savedRating: 0,
    pendingLowRating: false,
    comment: '',
    displayName: '',
    hasExistingReview: false,
    starButtons: Array.from(container.querySelectorAll('.tool-review__star')),
    dismissButton: container.querySelector('.tool-review__dismiss'),
    statusEl: container.querySelector('.tool-review__status'),
    docRef: null,
    clientIdHash: null,
    clientIdSource: null
  };

  widget.starButtons.forEach((button) => {
    button.addEventListener('click', () => {
      handleStarClick(widget, button.dataset.rating);
    });
  });
  widget.dismissButton?.addEventListener('click', () => {
    dismissWidget(widget);
  });

  // Firestore si collega solo quando l'utente si avvicina al widget: cosi' la
  // pagina non scarica l'SDK per un widget che la maggior parte non tocca.
  widget.connect = () => {
    if (!widget.connectPromise) widget.connectPromise = connectWidget(widget);
    return widget.connectPromise;
  };
  ['pointerenter', 'focusin', 'touchstart'].forEach((eventName) => {
    container.addEventListener(eventName, () => { widget.connect(); }, { once: true, passive: true });
  });

  return widget;
}

async function connectWidget(widget) {
  try {
    const { clientIdHash, source } = await getClientIdentity();
    const { db, firestore } = await getDb();
    const documentId = await sha256(`${window.location.origin}:${widget.toolSlug}:${clientIdHash}`);
    widget.clientIdHash = clientIdHash;
    widget.clientIdSource = source;
    widget.firestore = firestore;
    widget.docRef = firestore.doc(db, COLLECTION_NAME, documentId);
  } catch (error) {
    console.warn('Inizializzazione recensioni tool fallita', error);
    setStatus(widget, t('Recensioni temporaneamente non disponibili.'), 'error');
    widget.starButtons.forEach((button) => {
      button.disabled = true;
    });
    return false;
  }

  try {
    await hydrateExistingReview(widget);
  } catch (error) {
    console.warn('Recensione esistente non leggibile', error);
    setStatus(widget, '', '');
  }
  return true;
}

async function initWidget(container) {
  const toolSlug = container.dataset.toolSlug;
  const ignoreDismiss = container.dataset.toolReviewIgnoreDismiss === 'true';
  if (!toolSlug) return;
  if (!ignoreDismiss && isDismissed(toolSlug)) {
    container.hidden = true;
    return;
  }

  buildWidget(container);
  await setupWidget(container);
}

// Punto di aggancio per chi ha gia' il markup delle stelline in pagina (promo card).
// Cosi' il gating sulle recensioni basse resta in un posto solo.
export async function attachToolReview(container) {
  if (!container || container.dataset.toolReviewReady === 'true') return null;
  container.dataset.toolReviewReady = 'true';
  return setupWidget(container);
}

export function initToolReviews(root = document) {
  const containers = [];

  if (root instanceof Element && root.matches('[data-tool-review]:not([data-tool-review-ready])')) {
    containers.push(root);
  }

  root.querySelectorAll('[data-tool-review]:not([data-tool-review-ready])').forEach((container) => {
    containers.push(container);
  });

  containers.forEach((container) => {
    if (container.closest('.promo-card-container')) return;
    container.dataset.toolReviewReady = 'true';
    initWidget(container);
  });
}
